import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {isEmpty, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {omneticApi} from '@dms/api/base';
import {datagridApi, type KanbanSortBy} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';
import {BusinessCaseType} from '@dms/api/sales';
import {notificationTypes, useDataQuery, useIsCommunicationReadStateEnabled} from '@dms/shared';

import {useDebouncedCallback} from 'shared/web';

import {KANBAN_DEFAULT_SORT_BY} from '../constants/kanbanDefaultSortBy';
import {getKanbanOrder} from '../utils/getKanbanOrder';

const UNREAD_REFRESH_DEBOUNCE_MS = 500;

interface KanbanFilter {
  userIds: string[];
  sortBy: KanbanSortBy;
  branchIds: string[];
}

export const useKanbanControl = () => {
  const dispatch = useDispatch();
  const activeBranchId = useSelector(selectActiveBranchId);
  const [businessCaseType, setBusinessCaseType] = useState<BusinessCaseType>('SELLING');
  const [filter, setFilter] = useState<KanbanFilter | null>(null);
  const isReadStateEnabled = useIsCommunicationReadStateEnabled();

  // Requested only with the flags on: a grid that does not know a column rejects the whole query.
  const unreadColumns = isReadStateEnabled ? ['unreadMessageCount'] : [];

  // The cards poll once an hour, so the unread counts follow the read state pushes instead.
  // Debounced because one bulk change pushes one notification per affected message.
  const refreshUnreadCounts = useDebouncedCallback(() => {
    dispatch(
      omneticApi.util.invalidateTags([
        {type: 'DataGrid', id: 'business-case'},
        {type: 'DataGrid', id: 'interest'},
      ])
    );
  }, UNREAD_REFRESH_DEBOUNCE_MS);

  useSubscribeToNotificationsQuery((notification) => {
    if (
      !isReadStateEnabled ||
      notification.type !== notificationTypes.COMMUNICATION_UNREAD_CHANGED
    ) {
      return;
    }

    refreshUnreadCounts();
  });

  const branchIds = filter?.branchIds ?? [];
  const effectiveBranchIds = isEmpty(branchIds) ? [activeBranchId] : branchIds;

  const {
    dataQueryId: businessCaseQueryId,
    isLoading: isLoadingBusinessCaseQuery,
    isUninitialized: isBusinessCaseQueryUninitialized,
    isFetching: isFetchingBusinessCaseQuery,
    isError: isLoadingBusinessCaseQueryErrored,
  } = useDataQuery('business-case', {
    skip: isNil(filter),
    filters: {
      owner: filter?.userIds ?? [],
      businessCaseType: [businessCaseType],
      businessCaseState: [
        'OFFER',
        'INVOICE',
        'CONCEPT',
        'CONTRACT',
        'DEPOSIT',
        'DEPOSIT_PAID',
        'PAID',
      ],
      branch: effectiveBranchIds,
    },
    order: getKanbanOrder(filter?.sortBy ?? KANBAN_DEFAULT_SORT_BY, 'business-case'),
  });

  const {
    dataQueryId: interestQueryId,
    isLoading: isLoadingInterestQuery,
    isUninitialized: isInterestQueryUninitialized,
    isFetching: isFetchingInterestQuery,
    isError: isLoadingInterestQueryErrored,
  } = useDataQuery('interest', {
    skip: isNil(filter),
    filters: {
      owner: filter?.userIds ?? [],
      type: [businessCaseType],
      state: ['NEW', 'OPEN'],
      branch: effectiveBranchIds,
    },
    order: getKanbanOrder(filter?.sortBy ?? KANBAN_DEFAULT_SORT_BY, 'interest'),
  });

  const {
    data: businessCases,
    currentData: currentBusinessCases,
    isLoading: isLoadingBusinessCases,
    isFetching: isFetchingBusinessCases,
    isError: isLoadingBusinessCasesErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'business-case',
      dataQueryId: businessCaseQueryId!,
      offset: 0,
      limit: 1000,
      body: {
        columns: [
          'businessCaseState',
          'vehicle',
          'customerName',
          'tag',
          'comment',
          'note',
          'businessCasePrice',
          'vehiclePriceWithVat',
          'businessCaseUpdatedAt',
          'businessCaseCreatedAt',
          'lastActivityDate',
          'lastCommunicationDate',
          'lastCommunicationChannel',
          'owner',
          ...unreadColumns,
        ],
      },
    },
    {
      skip: isNilOrEmpty(businessCaseQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: KANBAN_POLLING_INTERVAL,
    }
  );

  const {
    data: interests,
    currentData: currentInterests,
    isLoading: isLoadingInterests,
    isFetching: isFetchingInterests,
    isError: isLoadingInterestsErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'interest',
      dataQueryId: interestQueryId!,
      offset: 0,
      limit: 1000,
      body: {
        columns: [
          'state',
          'type',
          'customer',
          'tag',
          'owner',
          'createdAt',
          'updatedAt',
          'comment',
          'note',
          'vehicle',
          'lastActivityDate',
          'lastCommunicationDate',
          'lastCommunicationChannel',
          ...unreadColumns,
        ],
      },
    },
    {
      skip: isNilOrEmpty(interestQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: KANBAN_POLLING_INTERVAL,
    }
  );

  const onFilterChange = (
    nextBusinessCaseType: BusinessCaseType,
    filteredUserIds: string[],
    sortBy: KanbanSortBy,
    branchIds: string[]
  ) => {
    if (isNilOrEmpty(filteredUserIds)) {
      return;
    }

    setFilter({userIds: filteredUserIds, sortBy, branchIds});
    setBusinessCaseType(nextBusinessCaseType);
  };

  return {
    businessCaseType,
    businessCases,
    interests,
    isLoading:
      isBusinessCaseQueryUninitialized ||
      isInterestQueryUninitialized ||
      isLoadingInterestQuery ||
      isLoadingBusinessCaseQuery ||
      isLoadingBusinessCases ||
      isLoadingInterests,
    isFetching:
      isFetchingBusinessCaseQuery ||
      isFetchingInterestQuery ||
      isFetchingBusinessCases ||
      isFetchingInterests,
    isApplyingFilter:
      isFetchingBusinessCaseQuery ||
      isFetchingInterestQuery ||
      (isFetchingBusinessCases && isNil(currentBusinessCases)) ||
      (isFetchingInterests && isNil(currentInterests)),
    isError:
      isLoadingInterestQueryErrored ||
      isLoadingBusinessCaseQueryErrored ||
      isLoadingBusinessCasesErrored ||
      isLoadingInterestsErrored,
    onFilterChange,
  };
};

/**
 * Polling interval for updates in milliseconds (60 minutes)
 * This is used to refresh the data in the kanban
 * This is needed because the kanban is not updated when the data is updated
 * and the data is not updated in the kanban when the user is not in the kanban
 *
 */
const KANBAN_POLLING_INTERVAL = 60 * 60 * 1000;
